import { useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, MailCheck } from "lucide-react";
import { ContactFooter } from "@/components/ContactFooter";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-md mx-auto px-5 pt-[14vh] fade-up">
        <div className="flex items-center gap-2 mb-10">
          <span className="inline-block w-3 h-3 rounded-full bg-[color:var(--volt)] border border-black/10" aria-hidden="true" />
          <span className="font-extrabold tracking-tight uppercase">Propedeutico</span>
        </div>

        {sent ? (
          <div data-testid="forgot-password-success">
            <div className="w-12 h-12 rounded-full bg-[color:var(--bg-subtle)] border border-[color:var(--hairline)] flex items-center justify-center mb-6">
              <MailCheck className="w-5 h-5" aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-extrabold tracking-tight leading-tight">Controlla la tua email</h1>
            <p className="mt-3 text-[color:var(--text-2)] leading-6">
              Se l'email <strong className="text-[color:var(--ink)]">{email}</strong> è registrata, riceverai a breve un link per
              reimpostare la password. Il link è valido per 1 ora. Controlla anche la posta indesiderata.
            </p>
            <Link
              to="/login"
              data-testid="forgot-password-back-to-login"
              className="press mt-8 inline-flex items-center justify-center rounded-full bg-[color:var(--ink)] text-white font-extrabold px-6 py-3.5 min-h-[44px] hover:bg-black/85 transition-colors duration-200"
            >
              Torna all'accesso
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-extrabold tracking-tight leading-tight">Password dimenticata?</h1>
            <p className="mt-3 text-[color:var(--text-2)] leading-6">
              Inserisci l'email con cui ti sei registrato: ti invieremo un link per scegliere una nuova password.
            </p>

            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold mb-1.5">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-password-email-input"
                  className="w-full rounded-2xl border border-[color:var(--hairline)] bg-white px-4 py-3.5 text-sm focus:border-[color:var(--ink)] outline-none transition-colors duration-200"
                  placeholder="nome@esempio.it"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="forgot-password-submit-button"
                className="press w-full rounded-full bg-[color:var(--ink)] text-white font-extrabold px-5 py-3.5 min-h-[44px] hover:bg-black/85 disabled:opacity-60 transition-colors duration-200"
              >
                {busy ? "Invio in corso…" : "Invia link di reset"}
              </button>
            </form>

            <Link
              to="/login"
              data-testid="forgot-password-goto-login-link"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--ink)] underline underline-offset-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" /> Torna all'accesso
            </Link>
          </>
        )}

        <ContactFooter />
        <p className="mt-6 pb-10 text-xs text-[color:var(--text-2)]">App di supporto alla preparazione. Non è un prodotto ufficiale FIGC.</p>
      </div>
    </div>
  );
}
